import { Search } from 'lucide-react';
import { OnboardingTabConfig } from '@/lib/dtos';
import { MockupTabBar } from '@/pages/onboarding/mockup/mockup-tab-bar';
import { getScreenCornerRadius } from '@/pages/onboarding/mockup/iphone-mockup';
import { IPhoneStatusBar } from '@/pages/onboarding/mockup/iphone-status-bar';

// Must match IPhoneMockup's own default `screenWidth` (no caller of
// AppOpenScene's page ever overrides it) — the radius below only matters
// while `enableIconMorph` is on, see that prop's own doc comment for why.
const SCREEN_CORNER_RADIUS = getScreenCornerRadius(300);

export interface AppOpenSceneProps {
  tabs: OnboardingTabConfig[];
  accentColor: string;
  /** Index of the tab to render as active — e.g. the one currently being edited. */
  activeIndex?: number;
  /** Forwarded to `MockupTabBar` — see its own doc comment. */
  onTabClick?: (index: number) => void;
  /** Forwarded to `MockupTabBar` — see its own doc comment. */
  onTabReorder?: (tabs: OnboardingTabConfig[]) => void;
  /**
   * Shares HomeScreenScene's icon `viewTransitionName` with this scene's
   * root, so navigating here from a home-screen step (via that navigate()
   * call's `viewTransition: true`) has the browser morph the tapped icon
   * into this full screen — the real iOS "app opening" zoom.
   *
   * Only TabBarConfigPage (the step that's actually arrived at from
   * AppLogoPage's icon) should set this. Every other page that renders
   * AppOpenScene (currently just NotificationPreviewPage) also navigates to
   * and from TabBarConfigPage with `viewTransition: true`, and the browser
   * treats two same-named elements across *any* view transition as one
   * persistent element to reposition — not just the icon-morph pair — so
   * tagging it there too made the browser silently run its own
   * position/size interpolation on this root in parallel with
   * NotificationPreviewPage's own hand-built slide-down, on a different
   * timeline the two could never actually agree on (see that page's
   * CENTER_IN_DELAY_MS/CENTER_TRANSITION_DURATION_MS). Defaults to `false`
   * so adding a new AppOpenScene caller doesn't silently opt back into that.
   */
  enableIconMorph?: boolean;
}

// "App open" scene: e-commerce-flavored content skeleton (search bar, promo
// banner, product grid) + the configured tab bar.
//
// The skeleton is a generic e-commerce placeholder, unrelated to the
// merchant's own store content, so it intentionally ignores `accentColor`
// (which only tints the active tab in the navigation bar below).
export function AppOpenScene({
  tabs,
  accentColor,
  activeIndex,
  onTabClick,
  onTabReorder,
  enableIconMorph = false,
}: AppOpenSceneProps) {
  return (
    <div
      className="flex h-full w-full flex-col"
      style={
        enableIconMorph
          ? {
              viewTransitionName: 'onboarding-app-icon',
              // The browser renders a view-transition-tagged element in its
              // own top-layer snapshot for the duration of the morph,
              // completely bypassing every ancestor's own clipping (the
              // phone screen wrapper's rounded `overflow-hidden` included —
              // see IPhoneMockup's own doc comment on `screenCornerRadius`
              // for the normal-DOM-flow version of this same problem). This
              // scene's root has to carry the phone's screen radius itself,
              // or the snapshot the browser captures is a plain rectangle:
              // its square corners read as poking out past the phone's
              // rounded bezel for as long as the morph is running, snapping
              // back to correctly clipped only once the transition ends and
              // normal layout (with its ancestor clipping) resumes.
              borderRadius: SCREEN_CORNER_RADIUS,
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <StoreContentSkeleton />
      <MockupTabBar
        tabs={tabs}
        accentColor={accentColor}
        activeIndex={activeIndex}
        onTabClick={onTabClick}
        onReorder={onTabReorder}
      />
    </div>
  );
}

/**
 * AppOpenScene with the chrome IPhoneMockup draws over it (status bar and
 * home indicator, light theme), for a scene that opens the app inside its
 * own screen (HomeScreenScene's `openApp`): it then matches the next step's
 * phone exactly.
 */
export function AppOpenScreen(props: AppOpenSceneProps) {
  return (
    <div className="relative h-full w-full">
      <AppOpenScene {...props} />
      <div className="pointer-events-none absolute inset-x-0 top-0">
        <IPhoneStatusBar theme="light" />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
        <div className="h-[5px] w-[134px] rounded-full bg-black/90" />
      </div>
    </div>
  );
}

function StoreContentSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden bg-background px-3 pb-3 pt-14">
      <div className="flex items-center gap-2 rounded-full bg-muted px-3 py-2">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="h-2 w-20 rounded-full bg-muted-foreground/20" />
      </div>

      <div className="flex h-16 shrink-0 flex-col justify-center gap-1.5 rounded-lg bg-muted px-3">
        <div className="h-2 w-24 rounded-full bg-muted-foreground/30" />
        <div className="h-1.5 w-16 rounded-full bg-muted-foreground/20" />
      </div>

      <div className="h-2 w-20 rounded-full bg-muted-foreground/25" />

      <div className="grid grid-cols-2 gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex flex-col gap-1.5 rounded-lg border border-border/60 p-1.5"
          >
            <div className="aspect-square rounded-md bg-muted" />
            <div className="h-1.5 w-3/4 rounded-full bg-muted-foreground/20" />
            <div className="h-1.5 w-1/2 rounded-full bg-muted-foreground/30" />
          </div>
        ))}
      </div>
    </div>
  );
}
